/**
 * useTableTheme.js
 * -----------------
 * Custom React hook for building a dynamic and flexible table theme
 * used with react-table-library (grid layout mode).
 *
 * Features:
 *  - Supports base columns and dynamic "extra" columns.
 *  - Ensures the TASK_NAME column is always included.
 *  - Reacts to "disabled" state by changing opacity and cursor.
 *  - Clean separation between configuration, logic, and style.
 */

import { createStandardColumns, StandardColumnIds } from "../helpers/columnUtils";

/**
 * Builds the CSS grid-template-columns value from the given column configurations.
 * 
 * @param {Array<Object>} baseColumnsConfig - Base column definitions.
 * @param {Array<Object>} extraColumnsConfig - Optional dynamic/extra column definitions.
 * @returns {string} A space-separated list of grid column widths.
 */
const buildGridTemplateColumns = (baseColumnsConfig, extraColumnsConfig) =>
  [baseColumnsConfig, extraColumnsConfig]
    .flatMap(cols => (cols || []).map(col => col.width || "auto"))
    .join(" ");

/**
 * Generates the theme object for a react-table-library Table.
 *
 * @param {Array<Object>} columns - Dynamic/extra columns passed to the table.
 * @param {boolean} disabled - If true, the table appears dimmed and uses a "wait" cursor.
 * @param {Array<string>} selectedBaseColumnIds - List of fieldIds representing which base columns to show.
 * @returns {Object} A theme object compatible with useTheme() or similar theming utilities.
 */
const useTableTheme = (columns, disabled, selectedBaseColumnIds) => {
  const filteredBaseColumns = createStandardColumns().filter(col =>
    col.fieldId === StandardColumnIds.TASK_NAME || selectedBaseColumnIds.includes(col.fieldId)
  );

  const gridTemplateColumns = buildGridTemplateColumns(filteredBaseColumns, columns);

  const cursor = disabled ? "wait" : "auto";
  const opacity = disabled ? 0.5 : 1;

  // Root CSS for the table component
  const tableStyle = `
    opacity: ${opacity};
    --data-table-library_grid-template-columns: ${gridTemplateColumns};
    & * { cursor: ${cursor}; }
  `;

  // Complete theme definition
  const tableViewTheme = {
    Table: tableStyle,
    Header: `
      font-size: 23px;
      font-weight: 500;
    `,
    Body: ``,
    BaseRow: `
      background-color: var(--theme-ui-colors-background);

      &.row-select-selected,
      &.row-select-single-selected {
        background-color: #e7e7f7;
        color: var(--theme-ui-colors-text);
        font-weight: normal;
      }
    `,
    Row: `
      font-size: 13px;
      color: var(--theme-ui-colors-text);

      &:first-of-type .td {
        border-top-color: #edeae9;
      }
      &:last-of-type .td {
        border-bottom-color: #edeae9;
      }
      & .td {
        padding: 0;
        border-bottom-color: #edeae97f;
        border-top-color:  #edeae97f;
        &.celltree > div > div > div:nth-child(2) {
          margin-right: 20px;
          width: 100%;
        }
      }

      &.drop-before .td {
        border-top-color: #bbb;
        background: linear-gradient(to bottom, #edeae9, #fff, #fff, #fff, #fff, #fff, #fff);
      }
      &.drop-after .td {
        border-bottom-color: #bbb;
        background: linear-gradient(to bottom, #fff, #fff, #fff, #fff, #fff, #fff, #edeae9);
      }
      &.drop-subtask .td {
        border-bottom-color: #bbb;
        border-top-color: #bbb;
      }
      &:hover {
        color: var(--theme-ui-colors-text-light);
      }
    `,
    BaseCell: `
      border-bottom: 1px solid transparent;
      border-top: 1px solid transparent;
      padding: 4px;

      svg {
        fill: var(--theme-ui-colors-text);
      }
    `,
    HeaderCell: `
      font-size: 13px !important;
      font-weight: 500 !important;
      text-transform: initial !important;
      padding: 0;
    `,
    Cell: `
      font-size: 13px;
      font-weight: 400;
    `,
    FooterCell: `
      font-size: 13px !important;
      font-weight: 500 !important;
    `,
  };

  return tableViewTheme;
};

export default useTableTheme;
